/**
 * Protected Route Component
 * 
 * Restricts access to routes based on authentication and role requirements.
 * Redirects unauthorized users to login or shows access denied.
 */

import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Shield, Lock, AlertTriangle, ArrowLeft, LogIn } from "lucide-react";

// Role hierarchy for access control
const ROLE_HIERARCHY = {
  admin: ["admin", "administrator"],
  staff: [
    "staff", "nurse", "doctor", "physician", "coordinator", 
    "medical_director", "social_worker", "chaplain", "provider",
    "rn", "lpn", "cna", "hha", "admissions", "scheduler", "quality"
  ],
  family: ["family", "caregiver", "patient"],
  facility: ["facility", "facility_admin"],
  physician: ["physician", "doctor", "medical_director"]
};

// Features restricted by role
const RESTRICTED_FEATURES = {
  // Features only admins can access
  admin_only: ["reports", "rcm", "billing", "user_management", "system_settings", "audit_logs"],
  // Features staff cannot access
  staff_restricted: ["reports", "rcm", "billing", "revenue", "financial"],
};

/**
 * Check if user has required role
 */
export function hasRequiredRole(userRole, requiredRoles) {
  if (!userRole || !requiredRoles || requiredRoles.length === 0) return false;
  
  const normalizedUserRole = userRole.toLowerCase();
  
  for (const required of requiredRoles) {
    // Direct match
    if (normalizedUserRole === required.toLowerCase()) return true;
    
    // Check role hierarchy
    const hierarchyRoles = ROLE_HIERARCHY[required.toLowerCase()];
    if (hierarchyRoles && hierarchyRoles.includes(normalizedUserRole)) return true;
  }
  
  return false;
}

/**
 * Check if user can access a specific feature
 */
export function canAccessFeature(userRole, featureId) {
  if (!userRole) return false;
  
  const normalizedRole = userRole.toLowerCase();
  const isAdmin = ROLE_HIERARCHY.admin.includes(normalizedRole);
  const isStaff = ROLE_HIERARCHY.staff.includes(normalizedRole);
  
  // Admins can access everything
  if (isAdmin) return true;
  
  // Check staff restrictions
  if (isStaff && RESTRICTED_FEATURES.staff_restricted.includes(featureId)) {
    return false;
  }
  
  return true;
}

/**
 * Access Denied Component
 */
function AccessDenied({ reason = "permission", onBack, onLogin }) {
  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <Card className="max-w-md w-full bg-slate-900 border-slate-700">
        <CardHeader className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
            {reason === "auth" ? (
              <Lock className="w-8 h-8 text-red-400" />
            ) : (
              <Shield className="w-8 h-8 text-red-400" />
            )}
          </div>
          <CardTitle className="text-white text-xl">
            {reason === "auth" ? "Authentication Required" : "Access Denied"}
          </CardTitle>
          <CardDescription className="text-slate-400">
            {reason === "auth" 
              ? "You need to log in to access this area."
              : "You don't have permission to access this feature."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5" />
              <div className="text-sm text-slate-300">
                {reason === "auth" ? (
                  <p>This section is restricted to authorized staff and administrators. Please log in with your credentials to continue.</p>
                ) : (
                  <p>This feature requires elevated permissions. If you believe you should have access, please contact your administrator.</p>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              className="flex-1 border-slate-700"
              onClick={onBack}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Go Back
            </Button>
            {reason === "auth" && (
              <Button 
                className="flex-1 bg-teal-600 hover:bg-teal-700"
                onClick={onLogin}
              >
                <LogIn className="w-4 h-4 mr-2" />
                Log In
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Protected Route Wrapper Component
 * 
 * @param {React.ReactNode} children - The component to render if authorized
 * @param {string[]} allowedRoles - Array of roles that can access this route
 * @param {string} featureId - Optional feature ID for granular access control
 * @param {string} redirectTo - Where to redirect if not authenticated (default: /login)
 */
export default function ProtectedRoute({ 
  children, 
  allowedRoles = ["admin", "staff"],
  featureId = null,
  redirectTo = "/login"
}) {
  const { user, isAuthenticated, loading } = useAuth();
  const location = useLocation();

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500" />
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated || !user) {
    return (
      <AccessDenied 
        reason="auth"
        onBack={() => window.history.back()}
        onLogin={() => window.location.href = `${redirectTo}?redirect=${encodeURIComponent(location.pathname)}`}
      />
    );
  }

  // Check role-based access
  const userRole = user.role || user.user_type || "unknown";
  const hasAccess = hasRequiredRole(userRole, allowedRoles);

  if (!hasAccess) {
    return (
      <AccessDenied 
        reason="permission"
        onBack={() => window.history.back()}
      />
    );
  }

  // Check feature-specific access
  if (featureId && !canAccessFeature(userRole, featureId)) {
    return (
      <AccessDenied 
        reason="permission"
        onBack={() => window.history.back()}
      />
    );
  }

  // Authorized - render children
  return children;
}

export { AccessDenied, ROLE_HIERARCHY, RESTRICTED_FEATURES };
